/**
 * @typedef {Readonly<{ collapsible?: boolean, onListItemClick?: () => void }>
 *  & import('./PropertyProvider/PropertyContext').PropType
 *  & import('../../shared/ThemeProvider/Context').PropType} Props
 */
/** @typedef {Readonly<{ isPropertySubmenuCollapsed: boolean }>} State */
/** @typedef {Omit<import('App/shared/NavigationListComponent').Props, 'location' | 'settings'>} NavigationListProps */
/** @typedef {NavigationListProps['links'][number]} NavigationLink */

import React from 'react';

import { translate } from 'App/Internationalization';
import NavigationList from 'App/shared/NavigationList';
import RoleGate from 'App/shared/RoleGate';
import navigation from 'config/local/navigation';
import { withProperty } from './PropertyProvider';
import { withTheme } from '../../shared/ThemeProvider';
import paths from '../../config/local/paths';
import { getPropertyScopedNavItems } from './Properties/propertyScopedNavigation';

/** @extends {React.Component<Props, State>} */
class HomeownerNavigationList extends React.Component {
	/** @param {Props} props */
	constructor(props) {
		super(props);

		this.state = {
			isPropertySubmenuCollapsed: false,
		};

		this.handlePropertyMenuClick = this.handlePropertyMenuClick.bind(this);
		this.handlePropertyMenuKeyDown =
			this.handlePropertyMenuKeyDown.bind(this);
	}

	/**
	 * @param {React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>} event
	 * @returns {void}
	 */
	handlePropertyMenuToggle(event) {
		event.preventDefault();
		event.stopPropagation();

		this.setState((state) => ({
			isPropertySubmenuCollapsed: !state.isPropertySubmenuCollapsed,
		}));
	}

	/**
	 * @param {React.MouseEvent<HTMLElement>} event
	 * @returns {void}
	 */
	handlePropertyMenuClick(event) {
		this.handlePropertyMenuToggle(event);
	}

	/**
	 * @param {React.KeyboardEvent<HTMLElement>} event
	 * @returns {void}
	 */
	handlePropertyMenuKeyDown(event) {
		if (event.key === 'Enter' || event.key === ' ') {
			this.handlePropertyMenuToggle(event);
		}
	}

	render() {
		const propertyId =
			this.props.property?.selectedProperty?.id ??
			this.props.property?.propertyList?.find(
				(property) => property.isPrimary
			)?.id ??
			this.props.property?.propertyList?.[0]?.id;
		/** @type {NavigationLink[]} */
		const links = navigation.homeowner.linkList
			.filter((link) => link !== null)
			.map((link) => /** @type {NavigationLink} */ (link))
			.flatMap((link) => {
				if (
					link.to !== paths.app.homeowner.properties.root ||
					!propertyId
				) {
					return [link];
				}

				const submenuIndicator = this.state.isPropertySubmenuCollapsed
					? '+'
					: '-';
				const propertyLink = {
					...link,
					exact: !this.state.isPropertySubmenuCollapsed,
					label: (
						<span className="nav-link-collapsible-label">
							<span>{link.label}</span>
							<span
								aria-expanded={
									!this.state.isPropertySubmenuCollapsed
								}
								aria-label={translate(
									'homeowner.nav.main.properties'
								)}
								className="nav-link-collapsible-indicator"
								onClick={this.handlePropertyMenuClick}
								onKeyDown={this.handlePropertyMenuKeyDown}
								role="button"
								tabIndex={0}
							>
								{submenuIndicator}
							</span>
						</span>
					),
				};

				if (this.state.isPropertySubmenuCollapsed) {
					return [propertyLink];
				}

				return [
					propertyLink,
					...getPropertyScopedNavItems().map((item) => ({
						icon: item.icon ?? link.icon,
						isSubmenu: true,
						key: `homeowner-properties-${item.key}`,
						label: item.label,
						to: item.path.replace(':propertyId', propertyId),
					})),
				];
			});

		return (
			<RoleGate allowedRoles={['homeowner']}>
				<NavigationList
					collapsible={this.props.collapsible}
					heading={translate('homeowner.nav.heading')}
					links={links}
					onListItemClick={this.props.onListItemClick}
				/>
			</RoleGate>
		);
	}
}

export default withTheme(withProperty(HomeownerNavigationList));
